import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { AssetsService } from "app/services/AssetsService";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { EducationalVideoModal } from "pro-features/components/Modals/src/EducationalVideoModal";
import type { IProMediaPlayerProps } from "pro-features/components/MediaPlayer/ProMediaPlayer";
import type { AssetType } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import {
    Container,
    Title,
    VideoThumbnail,
    LeanMoreLink,
} from "app/pages/digital-suite/home-page/components/CustomAssets/AssetsTooltipContent/styledComponents";

interface AssetsTooltipContentProps {
    assetType: AssetType;
    title: string;
    videoUrl: string;
    videoThumbnailSrc: string;
    learnMoreUrl: string;
    onVideoOpen: () => void;
}

export const AssetsTooltipContent: React.FC<AssetsTooltipContentProps> = ({
    assetType,
    videoUrl,
    videoThumbnailSrc,
    title,
    onVideoOpen,
    learnMoreUrl,
}) => {
    const [thumbnail, setThumbnail] = useState<string>("");
    const [isVideoOpen, setVideoOpen] = useState<boolean>(false);
    const videoProps = useMemo<IProMediaPlayerProps>(
        () => ({
            url: videoUrl,
            videoConfig: {
                autoPlay: true,
                width: "100%",
                height: "100%",
            },
        }),
        [videoUrl],
    );

    useEffect(() => {
        setThumbnail(AssetsService.assetUrl(videoThumbnailSrc));
    }, [videoThumbnailSrc]);

    return (
        <Container
            data-automation={`${assetType}-asset-tooltip`}
            onClick={(ev) => ev.stopPropagation()}
        >
            <Title>{title}</Title>
            <VideoThumbnail
                src={thumbnail}
                alt={title}
                onClick={(ev) => {
                    onVideoOpen();
                    setVideoOpen(true);
                    ev.stopPropagation();
                }}
            />
            <LeanMoreLink
                href={learnMoreUrl}
                target={"_blank"}
            >
                <I18n>digitalmarketing.homepage.custom_assets.empty_state.tooltip.learn_more</I18n>
            </LeanMoreLink>
            <EducationalVideoModal
                isOpen={isVideoOpen}
                onCloseClick={() => setVideoOpen(false)}
                videoConfig={videoProps}
                shouldCloseOnOverlayClick
            />
        </Container>
    );
};
